import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Grid,
  Avatar,
  Snackbar,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { userService } from '../services/UserService';
import { User } from '../types';

const SettingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, isLoading: authLoading } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  
  // 편집 가능한 필드
  const [loginName, setLoginName] = useState('');
  const [maxSelectNumber, setMaxSelectNumber] = useState('');
  // 포함/제외 희망 번호 (쉼표 구분 입력, 예: "7, 17, 27")
  const [includeNumbers, setIncludeNumbers] = useState('');
  const [excludeNumbers, setExcludeNumbers] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isMailSend, setIsMailSend] = useState(false);

  // "[7,17,27]" → "7, 17, 27"
  const jsonToCsv = (json?: string): string => {
    if (!json) return '';
    try {
      const arr = JSON.parse(json);
      return Array.isArray(arr) ? arr.join(', ') : '';
    } catch {
      return '';
    }
  };

  // "7, 17, 27" → [7,17,27] (1~45 범위/중복 제거)
  const csvToNums = (csv: string): number[] => {
    return Array.from(new Set(
      csv.split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isInteger(n) && n >= 1 && n <= 45)
    ));
  };

  useEffect(() => {
    if (authUser) {
      fetchUserInfo();
    }
  }, [authUser]);

  // 사용자 정보 가져오기
  const fetchUserInfo = async () => {
    try {
      setIsLoading(true);
      setError('');

      const userToken = localStorage.getItem('authToken');
      if (!userToken) {
        throw new Error('사용자 토큰이 없습니다.');
      }

      const userData = await userService.getUserInfo(userToken);
      setUser(userData);
      
      // 편집 필드 초기화
      setLoginName(userData.loginName || '');
      setMaxSelectNumber(userData.maxSelectNumber?.toString() || '0');
      setIncludeNumbers(jsonToCsv(userData.includeNumbers));
      setExcludeNumbers(jsonToCsv(userData.excludeNumbers));
      setPhoneNumber(userData.phoneNumber || '');
      setIsMailSend(userData.isMailSend || false);
    } catch (error) {
      console.error('사용자 정보 로드 실패:', error);
      setError(error instanceof Error ? error.message : '사용자 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // 사용자 정보 업데이트
  const handleUpdateUserInfo = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      const userToken = localStorage.getItem('authToken');
      if (!userToken) {
        throw new Error('사용자 토큰이 없습니다.');
      }
      
      // 입력값 검증
      if (!loginName.trim()) {
        throw new Error('이름을 입력해주세요.');
      }
      
      const maxSelectNum = parseInt(maxSelectNumber);
      if (isNaN(maxSelectNum) || maxSelectNum < 0) {
        throw new Error('최대 선택 가능 번호 수는 0 이상의 숫자여야 합니다.');
      }
      
      // 포함/제외 번호 파싱 (1~45, 중복 제거)
      const includeNums = csvToNums(includeNumbers);
      const excludeNums = csvToNums(excludeNumbers);

      // 포함번호는 한 게임(6개)에 모두 들어가야 하므로 6개 이하만 허용
      if (includeNums.length > 6) {
        throw new Error('포함 번호는 최대 6개까지 지정할 수 있습니다.');
      }
      // 같은 번호를 포함이자 제외로 동시에 지정할 수 없음
      if (includeNums.some((n) => excludeNums.includes(n))) {
        throw new Error('같은 번호를 포함과 제외에 동시에 지정할 수 없습니다.');
      }

      // 사용자 정보 업데이트
      const updatedUser = await userService.updateUserInfo(userToken, {
        loginName,
        maxSelectNumber: maxSelectNum,
        includeNumbers: includeNums,
        excludeNumbers: excludeNums,
        phoneNumber,
        isMailSend
      });
      
      setUser(updatedUser);
      setSuccess('사용자 정보가 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
    } catch (error) {
      console.error('사용자 정보 업데이트 실패:', error);
      setError(error instanceof Error ? error.message : '사용자 정보를 업데이트하는데 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // 편집 모드 토글
  const toggleEditMode = () => {
    if (isEditing) {
      // 편집 취소 시 원래 값으로 되돌림
      if (user) {
        setLoginName(user.loginName || '');
        setMaxSelectNumber(user.maxSelectNumber?.toString() || '0');
        setIncludeNumbers(jsonToCsv(user.includeNumbers));
        setExcludeNumbers(jsonToCsv(user.excludeNumbers));
        setPhoneNumber(user.phoneNumber || '');
        setIsMailSend(user.isMailSend || false);
      }
    }
    setIsEditing(!isEditing);
  };

  if (authLoading || isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Header title="설정" />
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            <Button 
              variant="outlined" 
              size="small" 
              sx={{ ml: 2 }} 
              onClick={fetchUserInfo}
            >
              다시 시도
            </Button>
          </Alert>
        )}
        
        {success && (
          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess('')}
            message={success}
          />
        )}
        
        {/* 사용자 프로필 정보 */}
        {user && (
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main' }}>
                {user?.loginName?.charAt(0) || '?'}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {isEditing ? (
                    <TextField
                      label="이름"
                      value={loginName}
                      onChange={(e) => setLoginName(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                  ) : (
                    user?.loginName || '이름 없음'
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user?.loginId || '아이디 없음'}
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Button 
                  variant={isEditing ? "outlined" : "contained"} 
                  color={isEditing ? "secondary" : "primary"}
                  onClick={toggleEditMode}
                  startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                  sx={{ mr: 1 }}
                >
                  {isEditing ? '취소' : '편집'}
                </Button>
                {isEditing && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleUpdateUserInfo}
                    disabled={isSaving}
                    startIcon={<SaveIcon />}
                  >
                    {isSaving ? '저장 중...' : '저장'}
                  </Button>
                )}
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">이메일</Typography>
                <Typography variant="body1">{user?.emailAddress || user?.email || '이메일 없음'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">전화번호</Typography>
                {isEditing ? (
                  <TextField
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="전화번호를 입력하세요"
                  />
                ) : (
                  <Typography variant="body1">{user?.phoneNumber || '전화번호 없음'}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">메일 수신</Typography>
                {isEditing ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isMailSend}
                        onChange={(e) => setIsMailSend(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={isMailSend ? "수신함" : "수신 안함"}
                  />
                ) : (
                  <Typography variant="body1">{user?.isMailSend ? '수신함' : '수신 안함'}</Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">최대 선택 가능 번호</Typography>
                {isEditing ? (
                  <TextField
                    type="number"
                    value={maxSelectNumber}
                    onChange={(e) => setMaxSelectNumber(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                ) : (
                  <Typography variant="body1">{user?.maxSelectNumber || 0}개</Typography>
                )}
              </Grid>
            </Grid>
            
            {/* 포함 / 제외 번호 섹션 (유료 등급에만 적용) */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>포함 / 제외 번호</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                번호 생성 시 항상 포함하거나 제외할 번호를 쉼표(,)로 구분해 입력하세요 (예: 7, 17, 27).
                유료(무료 제외) 등급에만 적용되며, 포함 번호는 한 게임(6개)에 모두 들어가야 하므로 최대 6개까지 가능합니다.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>포함 번호</Typography>
                  {isEditing ? (
                    <TextField
                      value={includeNumbers}
                      onChange={(e) => setIncludeNumbers(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="예: 7, 17, 27 (최대 6개)"
                    />
                  ) : (
                    <Typography variant="body1">{includeNumbers || '없음'}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>제외 번호</Typography>
                  {isEditing ? (
                    <TextField
                      value={excludeNumbers}
                      onChange={(e) => setExcludeNumbers(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="예: 4, 13, 44"
                    />
                  ) : (
                    <Typography variant="body1">{excludeNumbers || '없음'}</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}
        
        {/* 설정 메뉴 */}
        <Paper elevation={3}>
          <List>
            <ListItemButton onClick={() => navigate('/email-change')}>
              <ListItemIcon>
                <EmailIcon />
              </ListItemIcon>
              <ListItemText
                primary="이메일 변경"
                secondary="계정에 연결된 이메일 주소를 변경합니다."
              />
            </ListItemButton>
            
            <Divider />
            
            <ListItemButton onClick={() => navigate('/password-change')}>
              <ListItemIcon>
                <LockIcon />
              </ListItemIcon>
              <ListItemText
                primary="비밀번호 변경"
                secondary="계정 비밀번호를 변경합니다."
              />
            </ListItemButton>
          </List>
        </Paper>
      </Container>
    </Box>
  );
};

export default SettingsScreen; 